import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { FiltrosClientesReais, StatusLedCliente } from '@/hooks/useClientesReais';
import { useOrigensClientes } from '@/hooks/useClientesReais';

interface FiltrosClientesProps {
  filtros: FiltrosClientesReais;
  onFiltrosChange: (filtros: FiltrosClientesReais) => void;
}

const STATUS_OPTIONS_CONFIG: { value: StatusLedCliente; labelKey: string }[] = [
  { value: 'lead', labelKey: 'clients.statusLed.lead' },
  { value: 'ativo', labelKey: 'clients.statusLed.active' },
  { value: 'inativo', labelKey: 'clients.statusLed.inactive' },
  { value: 'pendente', labelKey: 'clients.statusLed.pending' },
];

const TIPO_OPTIONS_CONFIG: { value: 'lead' | 'cliente'; labelKey: string }[] = [
  { value: 'lead', labelKey: 'clients.filters.leads' },
  { value: 'cliente', labelKey: 'clients.filters.clients' },
];

/**
 * Painel de filtros avançados para /clientes. Renderiza só o conteúdo do
 * painel — o componente pai é responsável por o pôr dentro de um Popover (ou
 * Sheet) e ligar o trigger.
 *
 * A busca (`filtros.busca`) é gerida pelo pai — fica fora deste painel para
 * caber na toolbar principal.
 */
export function FiltrosClientes({ filtros, onFiltrosChange }: FiltrosClientesProps) {
  const { t } = useTranslation();
  const [dataPickerAberto, setDataPickerAberto] = useState<
    'apos' | 'antes' | 'contatoApos' | 'contatoAntes' | null
  >(null);
  const { data: origensDisponiveis } = useOrigensClientes();

  const temFiltrosAtivos =
    (filtros.status && filtros.status.length > 0) ||
    (filtros.tipo && filtros.tipo.length > 0) ||
    (filtros.origem && filtros.origem.length > 0) ||
    filtros.minServicos !== undefined ||
    filtros.maxServicos !== undefined ||
    filtros.minValor !== undefined ||
    filtros.maxValor !== undefined ||
    filtros.ultimoServicoApos !== undefined ||
    filtros.ultimoServicoAntes !== undefined ||
    filtros.primeiroContatoApos !== undefined ||
    filtros.primeiroContatoAntes !== undefined;

  const limparFiltros = () => {
    onFiltrosChange({
      busca: filtros.busca,
      mostrarArquivados: filtros.mostrarArquivados,
    });
  };

  const toggleStatus = (status: StatusLedCliente) => {
    const current = filtros.status || [];
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    onFiltrosChange({ ...filtros, status: next.length > 0 ? next : undefined });
  };

  const toggleTipo = (tipo: 'lead' | 'cliente') => {
    const current = filtros.tipo || [];
    const next = current.includes(tipo)
      ? current.filter((t) => t !== tipo)
      : [...current, tipo];
    onFiltrosChange({ ...filtros, tipo: next.length > 0 ? next : undefined });
  };

  const toggleOrigem = (origem: string) => {
    const current = filtros.origem || [];
    const next = current.includes(origem)
      ? current.filter((o) => o !== origem)
      : [...current, origem];
    onFiltrosChange({ ...filtros, origem: next.length > 0 ? next : undefined });
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">{t('clients.filters.advanced')}</h4>
        {temFiltrosAtivos && (
          <Button variant="ghost" size="sm" onClick={limparFiltros}>
            <X className="mr-1 h-3 w-3" />
            {t('clients.filters.clear')}
          </Button>
        )}
      </div>

      {/* Tipo: lead / cliente */}
      <div className="space-y-2">
        <Label>{t('common.type', 'Tipo')}</Label>
        <div className="flex flex-wrap gap-2">
          {TIPO_OPTIONS_CONFIG.map((option) => (
            <Button
              key={option.value}
              variant={filtros.tipo?.includes(option.value) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleTipo(option.value)}
              className="rounded-full"
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </div>
      </div>

      {/* Status LED */}
      <div className="space-y-2">
        <Label>{t('clients.filters.status')}</Label>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS_CONFIG.map((option) => (
            <Button
              key={option.value}
              variant={filtros.status?.includes(option.value) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleStatus(option.value)}
              className="rounded-full"
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </div>
      </div>

      {/* Origem */}
      {origensDisponiveis && origensDisponiveis.length > 0 && (
        <div className="space-y-2">
          <Label>{t('clients.filters.contactOrigin')}</Label>
          <div className="flex flex-wrap gap-2">
            {origensDisponiveis.map((origem) => (
              <Button
                key={origem}
                variant={filtros.origem?.includes(origem) ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleOrigem(origem)}
                className="rounded-full"
              >
                {origem}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Quantidade de serviços */}
      <div className="space-y-2">
        <Label>{t('clients.filters.serviceCount')}</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder={t('clients.filters.min')}
            value={filtros.minServicos ?? ''}
            onChange={(e) =>
              onFiltrosChange({
                ...filtros,
                minServicos: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="w-20"
          />
          <span className="text-muted-foreground">a</span>
          <Input
            type="number"
            placeholder={t('clients.filters.max')}
            value={filtros.maxServicos ?? ''}
            onChange={(e) =>
              onFiltrosChange({
                ...filtros,
                maxServicos: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="w-20"
          />
        </div>
      </div>

      {/* Valor total gasto */}
      <div className="space-y-2">
        <Label>{t('clients.filters.totalSpent')}</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder={t('clients.filters.min')}
            value={filtros.minValor ?? ''}
            onChange={(e) =>
              onFiltrosChange({
                ...filtros,
                minValor: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="w-20"
          />
          <span className="text-muted-foreground">a</span>
          <Input
            type="number"
            placeholder={t('clients.filters.max')}
            value={filtros.maxValor ?? ''}
            onChange={(e) =>
              onFiltrosChange({
                ...filtros,
                maxValor: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="w-20"
          />
        </div>
      </div>

      {/* Primeiro contato */}
      <div className="space-y-2">
        <Label>{t('clients.filters.firstContact')}</Label>
        <div className="flex flex-col gap-2">
          <Popover
            open={dataPickerAberto === 'contatoApos'}
            onOpenChange={(open) => setDataPickerAberto(open ? 'contatoApos' : null)}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="justify-start">
                {filtros.primeiroContatoApos
                  ? `Após ${format(filtros.primeiroContatoApos, 'dd/MM/yyyy')}`
                  : t('clients.filters.afterDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filtros.primeiroContatoApos}
                onSelect={(date) => {
                  onFiltrosChange({ ...filtros, primeiroContatoApos: date || undefined });
                  setDataPickerAberto(null);
                }}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
          <Popover
            open={dataPickerAberto === 'contatoAntes'}
            onOpenChange={(open) => setDataPickerAberto(open ? 'contatoAntes' : null)}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="justify-start">
                {filtros.primeiroContatoAntes
                  ? `Antes de ${format(filtros.primeiroContatoAntes, 'dd/MM/yyyy')}`
                  : t('clients.filters.beforeDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filtros.primeiroContatoAntes}
                onSelect={(date) => {
                  onFiltrosChange({ ...filtros, primeiroContatoAntes: date || undefined });
                  setDataPickerAberto(null);
                }}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Último serviço */}
      <div className="space-y-2">
        <Label>{t('clients.filters.lastService')}</Label>
        <div className="flex flex-col gap-2">
          <Popover
            open={dataPickerAberto === 'apos'}
            onOpenChange={(open) => setDataPickerAberto(open ? 'apos' : null)}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="justify-start">
                {filtros.ultimoServicoApos
                  ? `Após ${format(filtros.ultimoServicoApos, 'dd/MM/yyyy')}`
                  : t('clients.filters.afterDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filtros.ultimoServicoApos}
                onSelect={(date) => {
                  onFiltrosChange({ ...filtros, ultimoServicoApos: date || undefined });
                  setDataPickerAberto(null);
                }}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
          <Popover
            open={dataPickerAberto === 'antes'}
            onOpenChange={(open) => setDataPickerAberto(open ? 'antes' : null)}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="justify-start">
                {filtros.ultimoServicoAntes
                  ? `Antes de ${format(filtros.ultimoServicoAntes, 'dd/MM/yyyy')}`
                  : t('clients.filters.beforeDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filtros.ultimoServicoAntes}
                onSelect={(date) => {
                  onFiltrosChange({ ...filtros, ultimoServicoAntes: date || undefined });
                  setDataPickerAberto(null);
                }}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Mostrar arquivados */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="mostrar-arquivados"
          checked={filtros.mostrarArquivados || false}
          onCheckedChange={(checked) =>
            onFiltrosChange({
              ...filtros,
              mostrarArquivados: checked === true,
            })
          }
        />
        <Label htmlFor="mostrar-arquivados" className="cursor-pointer text-sm">
          {t('clients.filters.showArchived')}
        </Label>
      </div>
    </div>
  );
}
